import { Product, CategoryCategory, Category } from "@greeter/core";
import { adapt, notImplemented } from "@greeter/xstate-util";
import { assign, setup, emit, raise, assertEvent } from "xstate";
import { CategoryEditorApiRequirements } from "../CategoryEditor/CategoryEditor";
import {
  DeleteCategory,
  DeleteProduct,
  FetchCategories,
  FetchCategoryCategories,
  FetchProductsByCategory,
} from "@greeter/venue-api";
import { ProductEditorApiRequirements } from "../ProductEditor/ProductEditorMachine";

export interface ProductLibraryApiRequirements
  extends CategoryEditorApiRequirements,
    ProductEditorApiRequirements {
  fetchProductsByCategory: FetchProductsByCategory;
  fetchCategoryCategories: FetchCategoryCategories;
  fetchCategories: FetchCategories;
  deleteProduct: DeleteProduct;
  deleteCategory: DeleteCategory;
}

export function providApiForActor(
  m: typeof productLibraryMachine,
  api: ProductLibraryApiRequirements
) {
  return m.provide({
    actors: {
      fetchProductsByCategory: adapt(api.fetchProductsByCategory.bind(api)),
      fetchCategoryCategories: adapt(api.fetchCategoryCategories.bind(api)),
      fetchCategories: adapt(api.fetchCategories.bind(api)),
      deleteCategory: adapt(api.deleteCategory.bind(api)),
      deleteProduct: adapt(api.deleteProduct.bind(api)),
    },
  });
}

export type PickedProduct = {
  productId: string;
  productName: string;
  categoryId: string;
  categoryName: string;
};

type ProductLibraryEvents =
  | { type: "fetchCategories" }
  | { type: "fetchProductsByCategory" }
  | { type: "deleteCategory"; data: string }
  | { type: "fetchCategory"; data: string }
  | { type: "fetchCategoryCategories" }

  // Value used to pass along to the CategoryEditor
  // so that it knows from which category category this was called
  | { type: "setLastCategoryCategoryId"; data: string }
  | { type: "browseCategory"; categoryId: string }
  | {
      type: "editCategory";
      data?: { categoryId?: string; categoryCategoryId?: string };
    }
  | { type: "selectProduct"; productId: string }
  | { type: "editProduct"; productId?: string }
  | { type: "back" }
  | { type: "deleteProduct"; productId: string }
  | { type: "enableEditMode" }
  | { type: "enableBrowseMode" }
  | { type: "done" };

type ProductLibraryEmitted =
  | { type: "productSelected"; productId: string }
  | { type: "fetchedCategoryCategories" }
  | { type: "fetchedCategories" }
  | { type: "savedProduct" }
  | { type: "savedCategory" }
  | { type: "fetchedProductsByCategory" }
  | { type: "exit" }
  | { type: "done" };

type ProductLibraryMode = "browseMode" | "editMode" | "selectMode";

export type ProductLibraryContext = {
  productsByCategory: Map<string, Array<Product>>;
  categoryCategories: Array<CategoryCategory>;
  categories: Array<Category>;
  categoriesByCategoryCategory: Map<string, Array<Category>>;

  // Last clicked category category ID
  lastCategoryCategoryId?: string;

  mode: ProductLibraryMode;

  selectedProducts: Map<string, PickedProduct>;

  editingProductId?: string;
  editingCategoryId?: string;

  // The category we're browsing
  browsingCategoryId?: string;
};

function indexCategoriesByCategoryCategoryId(categories: Array<Category>) {
  const index = new Map<string, Array<Category>>();
  const uncategorizedId = "uncategorized";

  for (let i = 0, l = categories.length; i < l; i++) {
    const category = categories[i];
    const id = category.categoryCategoryId ?? uncategorizedId;
    const existing = index.get(id);

    if (existing) {
      existing.push(category);
    } else {
      index.set(id, [category]);
    }
  }

  return index;
}

export const productLibraryMachine = setup({
  types: {} as {
    context: ProductLibraryContext;
    events: ProductLibraryEvents;
    emitted: ProductLibraryEmitted;
  },
  actors: {
    fetchCategory:
      notImplemented<ProductLibraryApiRequirements["fetchCategory"]>(),
    deleteCategory:
      notImplemented<ProductLibraryApiRequirements["deleteCategory"]>(),
    fetchCategoryCategories:
      notImplemented<
        ProductLibraryApiRequirements["fetchCategoryCategories"]
      >(),
    fetchProductsByCategory:
      notImplemented<
        ProductLibraryApiRequirements["fetchProductsByCategory"]
      >(),
    fetchCategories:
      notImplemented<ProductLibraryApiRequirements["fetchCategories"]>(),
    deleteProduct:
      notImplemented<ProductLibraryApiRequirements["deleteProduct"]>(),
  },
}).createMachine({
  type: "parallel",

  context: {
    productsByCategory: new Map(),
    categories: [],
    categoryCategories: [],

    categoriesByCategoryCategory: new Map(),

    selectedProducts: new Map(),

    mode: "browseMode",
  },

  states: {
    api: {
      type: "parallel",

      states: {
        product: {
          initial: "idle",
          states: {
            idle: {
              on: {
                deleteProduct: "deleting",
              },
            },
            failed: {
              on: {
                deleteProduct: "deleting",
              },
            },
            deleting: {
              invoke: {
                src: "deleteProduct",
                input({ event }) {
                  assertEvent(event, "deleteProduct");
                  return [event.productId];
                },
                onError: "failed",
                onDone: {
                  target: "idle",
                  actions: raise({ type: "fetchProductsByCategory" }),
                },
              },
            },
          },
        },
        products: {
          initial: "idle",
          states: {
            idle: {
              on: {
                fetchProductsByCategory: {
                  target: "fetching",
                  guard({ context }) {
                    console.log(
                      `[productLibraryMachine] fetch products by category`,
                      context.browsingCategoryId
                    );
                    return !!context.browsingCategoryId;
                  },
                },
              },
            },
            failed: {
              after: {
                2_000: "fetching",
              },
            },
            fetching: {
              invoke: {
                src: "fetchProductsByCategory",
                input: ({ context }) => [context.browsingCategoryId!],
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ context, event }) => {
                      const newProductsByCategory = new Map<
                        string,
                        Array<Product>
                      >(context.productsByCategory);
                      console.debug(
                        "updating productsByCategory",
                        newProductsByCategory,
                        event.output
                      );
                      if (context.browsingCategoryId) {
                        newProductsByCategory.set(
                          context.browsingCategoryId!,
                          event.output
                        );
                        return {
                          productsByCategory: newProductsByCategory,
                        };
                      }

                      return {};
                    }),
                    emit({ type: "fetchedProductsByCategory" }),
                  ],
                },
                onError: "failed",
              },
            },
          },
        },
        categoryCategories: {
          initial: "fetching",
          states: {
            idle: {
              on: {
                fetchCategoryCategories: "fetching",
              },
            },
            failed: {
              after: { 2_000: "fetching" },
            },
            fetching: {
              invoke: {
                src: "fetchCategoryCategories",
                input() {
                  return [];
                },
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ event }) => {
                      return {
                        categoryCategories: event.output.concat([
                          {
                            id: "uncategorized",
                            name: "Øvrige",
                            priority: Number.MAX_SAFE_INTEGER,
                          },
                        ]),
                      };
                    }),
                    emit({ type: "fetchedCategoryCategories" }),
                  ],
                },
                onError: "idle",
              },
            },
          },
        },
        category: {
          initial: "idle",
          states: {
            idle: {
              on: {
                deleteCategory: "deleting",
              },
            },
            failed: {
              on: {
                deleteCategory: "deleting",
              },
            },
            deleting: {
              invoke: {
                src: "deleteCategory",
                input({ event }) {
                  assertEvent(event, "deleteCategory");
                  return [event.data];
                },
                onError: "idle",
                onDone: {
                  target: "idle",
                  actions: raise({ type: "fetchCategories" }),
                },
              },
            },
          },
        },
        categories: {
          initial: "fetching",
          states: {
            idle: {
              on: {
                fetchCategories: "fetching",
              },
            },
            failed: {
              after: { 2_000: "fetching" },
            },
            fetching: {
              invoke: {
                src: "fetchCategories",
                input() {
                  return [];
                },
                onDone: {
                  target: "idle",
                  actions: [
                    ({ event }) => {
                      console.debug("Fetched categories with result", event);
                    },
                    assign(({ event }) => ({
                      categories: event.output,
                      categoriesByCategoryCategory:
                        indexCategoriesByCategoryCategoryId(event.output),
                    })),
                    emit({ type: "fetchedCategories" }),
                  ],
                },
                onError: {
                  target: "failed",
                },
              },
            },
          },
        },
      },
    },
    ui: {
      initial: "browsingCategories",

      on: {
        enableEditMode: {
          actions: assign({ mode: "editMode" }),
        },
        enableBrowseMode: { actions: assign({ mode: "browseMode" }) },
        setLastCategoryCategoryId: {
          actions: assign({
            lastCategoryCategoryId: ({ event }) => event.data,
          }),
        },
        done: { actions: emit({ type: "done" }) },
      },

      states: {
        browsing: {},
        selecting: {},
        editing: {},
        browsingCategories: {
          on: {
            entry: raise({ type: "fetchCategories" }),
            back: { actions: emit({ type: "exit" }) },
            editCategory: {
              target: "editingCategory",
              actions: assign(({ event }) => ({
                editingCategoryId: event.data?.categoryId,
              })),
            },
            browseCategory: {
              target: "browsingProductsInCategory",
              actions: [
                assign(({ event }) => ({
                  browsingCategoryId: event.categoryId,
                })),
              ],
            },
          },
        },
        browsingProductsInCategory: {
          entry: raise({ type: "fetchProductsByCategory" }),
          on: {
            back: "browsingCategories",
            selectProduct: {
              actions: [
                assign(({ event, context }) => {
                  const selectedProducts = new Map(context.selectedProducts);

                  if (selectedProducts.has(event.productId)) {
                    selectedProducts.delete(event.productId);
                  } else {
                    selectedProducts.set(event.productId, {
                      productId: event.productId,
                      categoryId: context.browsingCategoryId ?? "",
                      productName:
                        context.productsByCategory
                          .get(context.browsingCategoryId ?? "")
                          ?.find((p) => p.id === event.productId)?.title ?? "",
                      categoryName:
                        context.categories.find(
                          (c) => c.id === context.browsingCategoryId
                        )?.name ?? "",
                    });
                  }

                  return { selectedProducts };
                }),
                emit(({ event }) => ({
                  type: "productSelected",
                  productId: event.productId,
                })),
              ],
            },
            editProduct: {
              target: "editingProduct",
              actions: assign(({ event }) => ({
                editingProductId: event.productId,
              })),
            },
          },
        },
        editingProduct: {
          on: {
            back: "browsingProductsInCategory",
            savedProduct: "browsingProductsInCategory",
          },
        },
        editingCategory: {
          on: {
            back: "browsingCategories",
            savedCategory: "browsingCategories",
          },
        },
      },
    },
  },
});
